import React, { FC, useRef, useState } from 'react';
import PropTypes, { number } from 'prop-types';

import * as UpChunk from '@mux/upchunk';
import MuxHelper from '../utils/MuxHelper';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { addMedia, createMediaRequested } from '../redux/reducers/media.slice';

type ModalUploadProps = {
  isOpen: boolean;
  toggle(): void;
};

// const accessToken = process.env.REACT_APP_MUX_TOKEN_ID;
// const secret = process.env.REACT_APP_MUX_TOKEN_SECRET;

const muxHelper = new MuxHelper();

const ModalUpload: FC<ModalUploadProps> = ({ isOpen, toggle }) => {
  let { id: projectId } = useParams<{ id: string }>();

  const [isUploading, setIsUploading] = useState(false);
  const [muxAssetId, setMuxAssetId] = useState<null | string>(null);
  const [isFocusVideoTitleInput, setIsFocusVideoTitleInput] = useState<boolean>(false);
  const [isHoverVideoTitleInput, setIsHoverVideoTitleInput] = useState<boolean>(false);
  const [isFocusVideoDescriptionInput, setIsFocusVideoDescriptionInput] = useState<boolean>(false);
  const [isHoverVideoDescriptionInput, setIsHoverVideoDescriptionInput] = useState<boolean>(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  const inputRef = useRef<null | HTMLInputElement>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const dispatch = useAppDispatch();

  async function handleChange(event: any) {
    if (!event.target.files[0]) return;

    setCurrentFile(event.target.files[0]);

    const uploadPromise = new Promise((resolve, reject) => {
      startUpload(event.target.files[0], { resolve, reject });
    });
    // event.target.value = '';

    toast.promise(uploadPromise, {
      loading: `Uploading`,
      success: <b>Video uploaded!</b>,
      error: <b>Upload failed!.</b>,
    });
  }

  async function startUpload(file: File, cb: { resolve: any; reject: any }) {
    const { id, url } = await muxHelper.fetchUpload();

    const upload = UpChunk.createUpload({
      endpoint: url,
      file,
    });

    upload.on('error', (err: any) => {
      console.log('err', { err });
      setIsUploading(false);
      cb.reject();
    });

    upload.on('progress', (progress: any) => {
      console.log('progress', progress.detail);
      setIsUploading(true);
    });

    upload.on('success', async () => {
      const asset = await muxHelper.fetchAssetsByUploadId(id);
      if (!asset?.playback_ids?.length) return;

      window.gtag('event', 'video_upload');

      setMuxAssetId(asset.id);
      setIsUploading(false);
      cb.resolve();
    });
  }

  async function onSubmit({ title, description }: { title: string; description: string }) {
    if (!muxAssetId) {
      toast.error('Must select a file');
      return;
    }

    const media: Media = { title, description, muxAssetsId: muxAssetId };

    dispatch(createMediaRequested({ media, projectId: +projectId }));
    toggle();
    reset();
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} className="modal-upload">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader toggle={toggle}>Upload video</ModalHeader>
          <ModalBody>
            <div>
              <div className="app-input">
                <div className="drag-drop-zone">
                  <input
                    style={{ display: 'none' }}
                    type="file"
                    onChange={handleChange}
                    ref={inputRef}
                    accept="video/*,.mp4"
                  />
                  <div
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={() => !currentFile && !isUploading && inputRef.current?.click()}
                  >
                    {isUploading && <div style={{ marginRight: 4 }} className="loader"></div>}
                    {(currentFile && <span>{currentFile.name}</span>) || (
                      <>
                        <span> Drag and drop a file, or </span>
                        <span style={{ paddingLeft: 4 }} className="highlight-text">
                          Browser
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ height: 12 }}></div>
              <div className="app-input">
                <label>
                  <strong>Title</strong>
                </label>
                <input
                  type="text"
                  {...register('title', { required: true })}
                  onBlur={() => setIsFocusVideoTitleInput(false)}
                  onFocus={() => setIsFocusVideoTitleInput(true)}
                  onMouseOver={() => setIsHoverVideoTitleInput(true)}
                  onMouseOut={() => setIsHoverVideoTitleInput(false)}
                />
                <span style={{ marginLeft: 4, flex: 1 }}>{errors.title && <small>Title is required.</small>}</span>
                <div
                  style={{
                    opacity: Number(isFocusVideoTitleInput && isHoverVideoTitleInput),
                  }}
                  className="app-input--hint"
                >
                  max 500 characters
                </div>
              </div>

              <div className="app-input">
                <label>
                  <strong>Description</strong>
                </label>
                <textarea
                  style={{ height: 120 }}
                  {...register('description', { required: true })}
                  onBlur={() => setIsFocusVideoDescriptionInput(false)}
                  onFocus={() => setIsFocusVideoDescriptionInput(true)}
                  onMouseOver={() => setIsHoverVideoDescriptionInput(true)}
                  onMouseOut={() => setIsHoverVideoDescriptionInput(false)}
                />
                <span style={{ marginLeft: 4, flex: 1 }}>
                  {errors.description && <small>Description is required.</small>}
                </span>
                <div
                  style={{
                    opacity: Number(isFocusVideoDescriptionInput && isHoverVideoDescriptionInput),
                  }}
                  className="app-input--hint"
                >
                  max 500 characters
                </div>
              </div>
              {/* <div className="app-input">
              <label>
                <strong>Visible to</strong>
              </label>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <span key={item} style={{ marginRight: 8 }}>
                  <input type="checkbox" /> <span>Tier #1</span>
                </span>
              ))}
            </div> */}
            </div>
          </ModalBody>
          <ModalFooter>
            <button type="submit" className="app-btn color-primary">
              Upload
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
};

ModalUpload.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

ModalUpload.defaultProps = {
  toggle: () => {},
};

export default ModalUpload;
